import { CreateCourseRequirement, DeleteCourseRequirement, UpdateCourseRequirement } from '@/connection/courses';
import { useAppSelector } from '@/store/hooks';
import { getUser } from '@/store/slices/user/selectors';
import { CourseRequirementType, ICourse, ICourseRequirement, ICreateCourseRequirement } from '@/types/courses';
import { BrandliveEvent } from '@/types/projects.types';
import { SessionTypesEnum } from '@/types/session.types';
import toastTrigger from '@ui/toast/toast-trigger';
import { useCallback, useState } from 'react';
import { getRequirementMapBySession } from '../utils/get-requirement-map-by-session';
import { DEFAULT_NEW_REQUIREMENT, defaultSettings } from '../utils/course-requirement-defaults';

type UseCourseRequirementProps = {
  course: ICourse | null;
  setCourse: (course: ICourse) => void;
  workingEvent: BrandliveEvent | null;
};

const useCourseRequirement = ({ course, setCourse, workingEvent }: UseCourseRequirementProps) => {
  const user = useAppSelector(getUser);

  const [modalOpen, setModalOpen] = useState(false);
  const [currentRequirement, setCurrentRequirement] = useState<ICreateCourseRequirement>(DEFAULT_NEW_REQUIREMENT);
  const [requirementWorking, setRequirementWorking] = useState(false);
  const [requirementToUpdate, setRequirementToUpdate] = useState<string | null>(null);
  const [requirementToDelete, setRequirementToDelete] = useState<string | null>(null);

  const canSaveNewRequirement = (requirement: ICreateCourseRequirement) =>
    Boolean(
      requirement.type === CourseRequirementType.watched_minute
        ? requirement.session_id && requirement.required_amount
        : requirement.session_id &&
            requirement.required_amount &&
            requirement.points &&
            requirement.required_amount <= requirement.points
    );

  const sessionHasStarted = useCallback(
    (requirement: ICourseRequirement | ICreateCourseRequirement) => {
      const session = workingEvent?.sessions.find((session) => session.session === requirement?.session_id);

      if (!session) return false;

      return !!(session.timestamp && session.timestamp < Date.now());
    },
    [workingEvent?.sessions]
  );

  const openRequirementModal = useCallback((requirement?: ICreateCourseRequirement) => {
    setCurrentRequirement(requirement || DEFAULT_NEW_REQUIREMENT);
    setModalOpen(true);
  }, []);

  const closeRequirementModal = useCallback(() => {
    setModalOpen(false);
    setCurrentRequirement(DEFAULT_NEW_REQUIREMENT);
    setRequirementToUpdate(null);
  }, []);

  const handleCreateRequirement = useCallback(async () => {
    if (!user?.active_channel || !course || !currentRequirement) return;

    if (sessionHasStarted(currentRequirement)) {
      toastTrigger(
        <div className="S1">
          Cannot change course requirements. Updating course requirements not permitted for courses that are currently
          live.
        </div>,
        {
          type: 'warning',
          autoClose: 10000,
        }
      );
      return;
    }

    setRequirementWorking(true);

    try {
      const createdRequirement = await CreateCourseRequirement(user.active_channel, course.uuid, currentRequirement);
      setCourse({
        ...course,
        requirements: [...(course.requirements || []), createdRequirement],
      });
      closeRequirementModal();
    } catch {
      toastTrigger(<div className="S1">Error adding course requirement</div>, {
        type: 'error',
        autoClose: 5000,
      });
    }

    setRequirementWorking(false);
  }, [user?.active_channel, course, currentRequirement, sessionHasStarted, setCourse, closeRequirementModal]);

  const handleUpdateRequirement = useCallback(async () => {
    if (!user?.active_channel || !course || !currentRequirement || !requirementToUpdate) return;

    if (sessionHasStarted(currentRequirement)) {
      toastTrigger(
        <div className="S1">
          Cannot change course requirements. Updating course requirements not permitted for courses that are currently
          live.
        </div>,
        {
          type: 'warning',
          autoClose: 10000,
        }
      );
      return;
    }

    setRequirementWorking(true);

    try {
      const updatedRequirement = await UpdateCourseRequirement(
        user.active_channel,
        course.uuid,
        requirementToUpdate,
        currentRequirement
      );
      setCourse({
        ...course,
        requirements: course.requirements?.map((req) => (req.uuid === requirementToUpdate ? updatedRequirement : req)),
      });
      closeRequirementModal();
    } catch {
      toastTrigger(<div className="S1">Error updating course requirement</div>, {
        type: 'error',
        autoClose: 5000,
      });
    }

    setRequirementWorking(false);
  }, [
    user?.active_channel,
    course,
    currentRequirement,
    requirementToUpdate,
    sessionHasStarted,
    setCourse,
    closeRequirementModal,
  ]);

  const handleDeleteRequirement = useCallback(async () => {
    if (!user?.active_channel || !course || !requirementToDelete) return;

    const requirement = course.requirements?.find((req) => req.uuid === requirementToDelete);
    if (!requirement) return;

    if (sessionHasStarted(requirement)) {
      toastTrigger(
        <div className="S1">
          Cannot change course requirements. Updating course requirements not permitted for courses that are currently
          live.
        </div>,
        {
          type: 'warning',
          autoClose: 10000,
        }
      );
      setRequirementToDelete(null);
      return;
    }

    try {
      setRequirementWorking(true);
      await DeleteCourseRequirement(user.active_channel, course.uuid, requirementToDelete);
      setCourse({
        ...course,
        requirements: course.requirements?.filter((req) => req.uuid !== requirementToDelete),
      });
      setRequirementToDelete(null);
    } catch {
      toastTrigger(<div className="S1">Error deleting course requirement</div>, {
        type: 'error',
        autoClose: 5000,
      });
    }
    setRequirementWorking(false);
  }, [user?.active_channel, course, requirementToDelete, sessionHasStarted, setCourse]);

  const handleEditRequirement = useCallback((requirement: ICourseRequirement) => {
    setRequirementToUpdate(requirement.uuid);
    setCurrentRequirement(requirement);
    setModalOpen(true);
  }, []);

  const canAddRequirement = (
    requirements: ICreateCourseRequirement[] | undefined,
    workingEvent: BrandliveEvent | undefined | null
  ) => {
    if (!workingEvent) {
      return false;
    }

    if (!requirements) {
      return true;
    }

    // create map to help prevent nested loops
    const requirementsMap = getRequirementMapBySession(requirements);

    // if every session has two requirements already, we can't add more
    for (const session of workingEvent.sessions) {
      // only broadcast sessions can have requirements as of right now
      if (session.session_type === SessionTypesEnum.broadcast) {
        // if the requirement map does not have two requirements on this session then the user
        // can add more requirements
        if (!requirementsMap[session.session]?.length || requirementsMap[session.session].length < 2) {
          return true;
        }
      }
    }

    return false;
  };

  return {
    modalOpen,
    currentRequirement,
    requirementWorking,
    requirementToUpdate,
    requirementToDelete,
    setCurrentRequirement,
    setRequirementToDelete,
    openRequirementModal,
    closeRequirementModal,
    handleCreateRequirement,
    handleUpdateRequirement,
    handleDeleteRequirement,
    handleEditRequirement,
    canAddRequirement,
    canSaveNewRequirement,
    sessionHasStarted,
    defaultSettings,
    defaultNewRequirement: DEFAULT_NEW_REQUIREMENT,
  };
};

export default useCourseRequirement;
